import React, { useState } from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setActiveSection(activeSection === section ? null : section);
  };

  const lastUpdated = "December 15, 2024";

  return (
    <div className="privacy-policy-container">
      <div className="privacy-header">
        <div className="header-content">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last updated: {lastUpdated}</p>
        </div>
      </div>

      <div className="privacy-content">
        <div className="privacy-intro">
          <p>
            At Sportagram, we take your privacy seriously. This Privacy Policy explains how we 
            collect, use, disclose, and safeguard your information when you use our platform, 
            website, and mobile applications.
          </p>
          <p>
            By using Sportagram, you consent to the data practices described in this policy. 
            If you do not agree with our policies and practices, please do not use our Service.
          </p>
        </div>

        <div className="privacy-sections">
          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('collection')}
            >
              <h2>1. Information We Collect</h2>
              <span className={`toggle-icon ${activeSection === 'collection' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'collection' && (
              <div className="section-content">
                <h4>Personal Information</h4>
                <p>We collect information you provide directly to us, such as:</p>
                <ul>
                  <li>Name, email address, and profile information</li>
                  <li>Sports preferences and athletic achievements</li>
                  <li>Photos, videos, and other content you upload</li>
                  <li>Messages and communications with other users</li>
                  <li>Payment information for premium features</li>
                </ul>
                
                <h4>Automatically Collected Information</h4>
                <ul>
                  <li>Device information (IP address, browser type, operating system)</li>
                  <li>Usage data (pages visited, features used, time spent)</li>
                  <li>Location data (with your permission)</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>
            )}
          </div>

          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('usage')}
            >
              <h2>2. How We Use Your Information</h2>
              <span className={`toggle-icon ${activeSection === 'usage' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'usage' && (
              <div className="section-content">
                <p>We use the information we collect to:</p>
                <ul>
                  <li>Provide, maintain, and improve our Service</li>
                  <li>Create and manage your account</li>
                  <li>Connect you with other athletes and sports enthusiasts</li>
                  <li>Personalize your experience and content recommendations</li>
                  <li>Send you notifications and updates</li>
                  <li>Process payments and transactions</li>
                  <li>Provide customer support</li>
                  <li>Ensure platform safety and security</li>
                  <li>Comply with legal obligations</li>
                  <li>Conduct research and analytics to improve our Service</li>
                </ul>
              </div>
            )}
          </div>

          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('sharing')}
            >
              <h2>3. Information Sharing and Disclosure</h2>
              <span className={`toggle-icon ${activeSection === 'sharing' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'sharing' && (
              <div className="section-content">
                <p>We may share your information in the following circumstances:</p>
                
                <h4>With Other Users</h4>
                <p>
                  Your profile information, posts, and activities may be visible to other 
                  Sportagram users based on your privacy settings.
                </p>
                
                <h4>With Service Providers</h4>
                <p>
                  We may share information with third-party service providers who help us 
                  operate our platform, such as hosting, analytics, and payment processing.
                </p>
                
                <h4>For Legal Reasons</h4>
                <p>We may disclose information if required by law or to:</p>
                <ul>
                  <li>Comply with legal processes or government requests</li>
                  <li>Protect our rights, property, or safety</li>
                  <li>Prevent fraud or abuse</li>
                  <li>Enforce our Terms of Use</li>
                </ul>
                
                <h4>Business Transfers</h4>
                <p>
                  In the event of a merger, acquisition, or sale of assets, your information 
                  may be transferred as part of that transaction.
                </p>
              </div>
            )}
          </div>

          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('security')}
            >
              <h2>4. Data Security</h2>
              <span className={`toggle-icon ${activeSection === 'security' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'security' && (
              <div className="section-content">
                <p>
                  We implement appropriate technical and organizational security measures to 
                  protect your personal information against unauthorized access, alteration, 
                  disclosure, or destruction.
                </p>
                
                <h4>Security Measures Include:</h4>
                <ul>
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security audits and assessments</li>
                  <li>Access controls and authentication systems</li>
                  <li>Employee training on data protection</li>
                  <li>Incident response procedures</li>
                </ul>
                
                <p>
                  However, no method of transmission over the internet or electronic storage 
                  is 100% secure. While we strive to protect your information, we cannot 
                  guarantee absolute security.
                </p>
              </div>
            )}
          </div>

          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('rights')}
            >
              <h2>5. Your Privacy Rights</h2>
              <span className={`toggle-icon ${activeSection === 'rights' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'rights' && (
              <div className="section-content">
                <p>Depending on your location, you may have the following rights:</p>
                
                <h4>Access and Portability</h4>
                <p>Request access to your personal information and receive a copy in a portable format.</p>
                
                <h4>Correction</h4>
                <p>Request correction of inaccurate or incomplete personal information.</p>
                
                <h4>Deletion</h4>
                <p>Request deletion of your personal information, subject to certain exceptions.</p>
                
                <h4>Restriction</h4>
                <p>Request restriction of processing of your personal information.</p>
                
                <h4>Objection</h4>
                <p>Object to processing of your personal information for certain purposes.</p>
                
                <h4>Withdraw Consent</h4>
                <p>Withdraw consent for processing where we rely on your consent.</p>
                
                <p>
                  To exercise these rights, please contact us at privacy@sportagram.com. 
                  We will respond to your request within 30 days.
                </p>
              </div>
            )}
          </div>

          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('cookies')}
            >
              <h2>6. Cookies and Tracking Technologies</h2>
              <span className={`toggle-icon ${activeSection === 'cookies' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'cookies' && (
              <div className="section-content">
                <p>
                  We use cookies and similar tracking technologies to enhance your experience 
                  on our platform. Cookies are small data files stored on your device.
                </p>
                
                <h4>Types of Cookies We Use:</h4>
                <ul>
                  <li><strong>Essential Cookies:</strong> Necessary for the platform to function</li>
                  <li><strong>Performance Cookies:</strong> Help us analyze how you use our Service</li>
                  <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
                  <li><strong>Advertising Cookies:</strong> Deliver relevant advertisements</li>
                </ul>
                
                <p>
                  You can control cookies through your browser settings. However, disabling 
                  certain cookies may affect the functionality of our Service.
                </p>
              </div>
            )}
          </div>

          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('children')}
            >
              <h2>7. Children's Privacy</h2>
              <span className={`toggle-icon ${activeSection === 'children' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'children' && (
              <div className="section-content">
                <p>
                  Our Service is not intended for children under 13 years of age. We do not 
                  knowingly collect personal information from children under 13.
                </p>
                <p>
                  If you are a parent or guardian and believe your child has provided us with 
                  personal information, please contact us immediately. We will take steps to 
                  remove such information from our systems.
                </p>
                <p>
                  For users between 13 and 18, we recommend parental guidance and supervision 
                  when using our Service.
                </p>
              </div>
            )}
          </div>

          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('international')}
            >
              <h2>8. International Data Transfers</h2>
              <span className={`toggle-icon ${activeSection === 'international' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'international' && (
              <div className="section-content">
                <p>
                  Your information may be transferred to and processed in countries other than 
                  your own. These countries may have different data protection laws.
                </p>
                <p>
                  When we transfer your information internationally, we ensure appropriate 
                  safeguards are in place to protect your privacy rights, including:
                </p>
                <ul>
                  <li>Standard contractual clauses approved by regulatory authorities</li>
                  <li>Adequacy decisions by relevant data protection authorities</li>
                  <li>Certification schemes and codes of conduct</li>
                </ul>
              </div>
            )}
          </div>

          <div className="section">
            <div 
              className="section-header"
              onClick={() => toggleSection('changes')}
            >
              <h2>9. Changes to This Privacy Policy</h2>
              <span className={`toggle-icon ${activeSection === 'changes' ? 'active' : ''}`}>
                ▼
              </span>
            </div>
            {activeSection === 'changes' && (
              <div className="section-content">
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of 
                  any material changes by posting the new Privacy Policy on this page and 
                  updating the "Last updated" date.
                </p>
                <p>
                  For significant changes, we may also send you an email notification or 
                  display a prominent notice on our Service.
                </p>
                <p>
                  Your continued use of the Service after any changes constitutes acceptance 
                  of the updated Privacy Policy.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="contact-section">
          <h3>Contact Us About Privacy</h3>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy 
            or our data practices, please contact us:
          </p>
          <div className="contact-details">
            <p><strong>Privacy Officer:</strong> privacy@sportagram.com</p>
            <p><strong>Data Protection Officer:</strong> dpo@sportagram.com</p>
            <p><strong>Address:</strong> Sportagram Inc., 123 Sports Avenue, Athletic City, AC 12345</p>
            <p><strong>Phone:</strong> +1 (555) 123-SPORT</p>
          </div>
          
          <div className="gdpr-notice">
            <h4>For EU Residents (GDPR)</h4>
            <p>
              If you are located in the European Union, you have additional rights under the 
              General Data Protection Regulation (GDPR). You may contact our EU representative at:
            </p>
            <p><strong>EU Representative:</strong> eu-privacy@sportagram.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;